import { Dropdown, Button, Popconfirm, Modal } from 'antd';
import type { MenuProps } from 'antd';
import { IconChevronDown } from '@tabler/icons-react';
import type { CSSProperties } from 'react';
import type { CaseStatus, DeclarationCase } from '@/api/cases';
import { isTerminal } from '@/api/cases';
import { canGenerateArtifacts } from '@/api/artifacts';
import type { Role } from '@/lib/role';

/**
 * 卡片操作菜单 — 按当前 status + 当前 role 过滤可用操作。
 *
 * RBAC（与 backend_app/app/core/auth.py 对位）：
 *   - STAFF: case:read/create/edit/freeze/submit/withdraw — 无 start_review/record_review
 *   - TENANT_ADMIN + PLATFORM: 全套
 *   - ENTERPRISE: 空集（页面级 RoleGuard 拦截，根本进不来）
 *
 * 真守卫在后端 require_permission；前端仅是 UX 不晃眼。
 */
export interface CaseActionsMenuProps {
  case_: DeclarationCase;
  role: Role | null;
  onFreeze: () => void;
  onSubmit: () => void;
  onStartReview: () => void;
  onRecordReview: () => void; // 实际打开 RecordReviewModal
  onWithdraw: () => void; // 实际打开 WithdrawModal
  onDelete: () => void;
  /**
   * M3-ρ-4：触发打开 GenerateArtifactsModal（仅 status ∈ frozen+ 显示）。
   * 可选 — 未传则不渲染生成按钮（兼容旧调用方/已有单测）。
   */
  onGenerateArtifacts?: () => void;
  pending?: boolean;
}

type ActionKey =
  | 'freeze'
  | 'submit'
  | 'start-review'
  | 'record-review'
  | 'withdraw'
  | 'delete'
  | 'generate-artifacts';

interface ActionDef {
  key: ActionKey;
  label: string;
  danger?: boolean;
  /** 后端 require_permission 对应权限点 */
  requiredPerm: string;
}

/**
 * 状态机可达动作（与 backend_app/app/schemas/declaration_case.py:_ALLOWED_TRANSITIONS 对位）。
 * 终止态（approved/rejected/withdrawn）返回空数组 — 卡片无任何操作按钮。
 */
function availableActions(status: CaseStatus): ActionDef[] {
  switch (status) {
    case 'draft':
      return [
        { key: 'freeze', label: '冻结', requiredPerm: 'case:freeze' },
        { key: 'withdraw', label: '撤回', requiredPerm: 'case:withdraw' },
        { key: 'delete', label: '删除', danger: true, requiredPerm: 'case:edit' },
      ];
    case 'frozen':
      return [
        { key: 'submit', label: '提交', requiredPerm: 'case:submit' },
        { key: 'withdraw', label: '撤回', requiredPerm: 'case:withdraw' },
      ];
    case 'submitted':
      return [
        { key: 'start-review', label: '开始审查', requiredPerm: 'case:start_review' },
        { key: 'withdraw', label: '撤回', requiredPerm: 'case:withdraw' },
      ];
    case 'reviewing':
      return [
        {
          key: 'record-review',
          label: '记录审查结果',
          requiredPerm: 'case:record_review',
        },
        { key: 'withdraw', label: '撤回', requiredPerm: 'case:withdraw' },
      ];
    default:
      return [];
  }
}

/**
 * 角色 → 权限点白名单（与后端 ROLE_PERMISSIONS 对位 — 仅 case:* 子集）。
 * 注：MANAGER / INVESTOR / MENTOR 等前端额外角色后端 case 权限矩阵未列 → 默认无 case 权限
 *     （他们走自己的子页面，不进 /cases）。
 */
const ROLE_CASE_PERMS: Record<string, Set<string>> = {
  platform_operator: new Set([
    'case:read',
    'case:create',
    'case:edit',
    'case:freeze',
    'case:submit',
    'case:start_review',
    'case:record_review',
    'case:withdraw',
    // M3-ρ-4
    'artifact:generate',
    'artifact:read',
    'artifact:delete',
  ]),
  tenant_admin: new Set([
    'case:read',
    'case:create',
    'case:edit',
    'case:freeze',
    'case:submit',
    'case:start_review',
    'case:record_review',
    'case:withdraw',
    'artifact:generate',
    'artifact:read',
  ]),
  staff: new Set([
    'case:read',
    'case:create',
    'case:edit',
    'case:freeze',
    'case:submit',
    'case:withdraw',
    'artifact:generate',
    'artifact:read',
  ]),
};

function roleHasPerm(role: Role | null, perm: string): boolean {
  if (!role) return false;
  const set = ROLE_CASE_PERMS[role];
  return !!set && set.has(perm);
}

const btnStyle: CSSProperties = {
  fontSize: 'var(--font-size-xs)',
  height: 26,
  padding: '0 10px',
};

export function CaseActionsMenu({
  case_,
  role,
  onFreeze,
  onSubmit,
  onStartReview,
  onRecordReview,
  onWithdraw,
  onDelete,
  onGenerateArtifacts,
  pending,
}: CaseActionsMenuProps) {
  // 状态机动作（不含 generate-artifacts —— 它跨状态、不参与 _ALLOWED_TRANSITIONS）
  const stateActions = isTerminal(case_.status)
    ? []
    : availableActions(case_.status);

  // M3-ρ-4：generate-artifacts 在 frozen+ 全部状态都允许（含终止态）。
  // 后端守卫 canGenerateArtifacts() 反映这点（draft 拒、其他放行）。
  // 仅当父组件提供 onGenerateArtifacts 回调时才显示（兼容旧调用方）。
  const artifactActions: ActionDef[] =
    onGenerateArtifacts && canGenerateArtifacts(case_.status)
      ? [
          {
            key: 'generate-artifacts',
            label: '生成材料',
            requiredPerm: 'artifact:generate',
          },
        ]
      : [];

  const allActions = [...stateActions, ...artifactActions];
  // RBAC 过滤：仅展示当前角色有权限的操作
  const allowedActions = allActions.filter((a) => roleHasPerm(role, a.requiredPerm));

  if (allowedActions.length === 0) {
    // 终止态 + 无 artifact 权限 → 完全不渲染（保持 ρ-3 AC 不变量）
    if (isTerminal(case_.status)) return null;
    return (
      <span
        style={{
          fontSize: 'var(--font-size-xs)',
          color: 'var(--color-text-tertiary)',
        }}
      >
        无可用操作
      </span>
    );
  }

  const dispatch = (key: ActionKey) => {
    switch (key) {
      case 'freeze':
        onFreeze();
        break;
      case 'submit':
        onSubmit();
        break;
      case 'start-review':
        onStartReview();
        break;
      case 'record-review':
        onRecordReview();
        break;
      case 'withdraw':
        onWithdraw();
        break;
      case 'delete':
        onDelete();
        break;
      case 'generate-artifacts':
        onGenerateArtifacts?.();
        break;
    }
  };

  // 主操作 = 第一个（状态机正向推进）；其余进入下拉
  const [primary, ...rest] = allowedActions;
  if (!primary) return null;

  const items: MenuProps['items'] = rest.map((a) => ({
    key: a.key,
    label: a.label,
    danger: a.danger,
  }));

  return (
    <span style={{ display: 'inline-flex', gap: 6 }}>
      {primary.key === 'delete' ? (
        <Popconfirm
          title="删除草稿申报案？"
          description="仅 draft 可删；删除后不可恢复。"
          okText="确认删除"
          cancelText="取消"
          okButtonProps={{ danger: true }}
          onConfirm={onDelete}
        >
          <Button danger size="small" style={btnStyle} disabled={pending}>
            {primary.label}
          </Button>
        </Popconfirm>
      ) : (
        <Button
          type="primary"
          size="small"
          style={btnStyle}
          loading={pending}
          onClick={() => dispatch(primary.key)}
        >
          {primary.label}
        </Button>
      )}
      {items.length > 0 && (
        <Dropdown
          menu={{
            items,
            // M3-ρ-3 Test Writer 发现 bug：当 primary != delete 时，删除被推入下拉，
            // 原 onClick 直接派发 dispatch('delete') 绕过 Popconfirm。这里加二次确认守底线。
            onClick: ({ key }) => {
              const k = key as ActionKey;
              if (k === 'delete') {
                Modal.confirm({
                  title: '删除草稿申报案？',
                  content: '仅 draft 可删；删除后不可恢复。',
                  okText: '确认删除',
                  cancelText: '取消',
                  okButtonProps: { danger: true },
                  onOk: () => dispatch('delete'),
                });
                return;
              }
              dispatch(k);
            },
          }}
          trigger={['click']}
          disabled={pending}
        >
          <Button size="small" style={btnStyle}>
            更多 <IconChevronDown size={12} />
          </Button>
        </Dropdown>
      )}
    </span>
  );
}
